/**
 * Hook do histórico mensal de PLD (Task #190 Fase 2 — consome
 * /api/v2/pld/historico). Cache keyed por (meses, submercado). TTL 5 min.
 *
 * `usePldHistoricoAll` chama 4× (SE/S/NE/N) pra sub-tab Histórico (gráfico +
 * tabela com 4 séries); combinação client-side porque o endpoint é por
 * submercado.
 */
import { useEffect, useState } from "react";
import { getPldHistorico } from "../api/client";
import type { PldHistoricoResponse, PldSubmercado } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldHistoricoResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldHistoricoCache(): void {
  cache.clear();
}

export interface UsePldHistoricoState {
  data: PldHistoricoResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldHistorico(
  meses: number,
  submercado: string,
): UsePldHistoricoState {
  const [state, setState] = useState<UsePldHistoricoState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = `${meses}|${submercado}`;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getPldHistorico(meses, submercado),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [meses, submercado]);

  return state;
}

export interface UsePldHistoricoAllState {
  bySubmercado: Record<PldSubmercado, PldHistoricoResponse | null>;
  isLoading: boolean;
  error: Error | null;
}

/** Histórico das 4 submercados — para o gráfico + tabela da sub-tab Histórico. */
export function usePldHistoricoAll(meses: number): UsePldHistoricoAllState {
  const se = usePldHistorico(meses, "SE");
  const s = usePldHistorico(meses, "S");
  const ne = usePldHistorico(meses, "NE");
  const n = usePldHistorico(meses, "N");
  return {
    bySubmercado: { SE: se.data, S: s.data, NE: ne.data, N: n.data },
    isLoading: se.isLoading || s.isLoading || ne.isLoading || n.isLoading,
    error: se.error ?? s.error ?? ne.error ?? n.error,
  };
}
